import { Step } from 'react-joyride';

export interface OnboardingStep extends Step {
    route?: string;
}

export const getOnboardingSteps = (menuList: any[]): OnboardingStep[] => {
    const isModuleEnabled = (code: string): boolean => {
        const menu = menuList?.find((m: any) => m.code === code);
        if (!menu) return false;
        return menu.is_enabled && (
            (menu.subMenus && menu.subMenus.some((sub: any) => sub.is_enabled)) ||
            (menu.sub_menus && menu.sub_menus.some((sub: any) => sub.is_enabled))
        );
    };

    const steps: OnboardingStep[] = [
        {
            target: '.module-nav',
            title: '🧭 Module Navigation',
            content:
                'Switch between POS, Inventory, Staffing, CRM, Finance, and Restaurant Admin modules from this top bar. The modules dynamically adapt based on your account permissions.',
            placement: 'bottom',
            skipBeacon: true,
        },
        {
            target: '#vertical_menu',
            title: '📋 Sidebar Menu',
            content:
                'Access all specific pages and settings for the active module here. Hover over the sidebar to expand and view the full menu structure.',
            placement: 'right',
            skipBeacon: true,
        },
        {
            target: 'body',
            title: '💻 Dynamic Workspace',
            content:
                'This is your main viewport where forms, tables, reports, and page content load dynamically. Everything changes seamlessly as you navigate different sections.',
            placement: 'center',
            skipBeacon: true,
        },
    ];

    // Conditional Inventory steps (Mandatory Steps 1 & 2)
    if (isModuleEnabled('INVENTORY')) {
        steps.push({
            target: '.tour-module-INVENTORY',
            title: '📦 Inventory Module',
            content:
                'Click this tab to open the Inventory & Waste Management module to set up your suppliers, invoicing, and raw material list.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/inventory/supply-chain-management/supplier-management',
        });
        steps.push({
            target: '.tour-INVENTORY_SUPPLY_CHAIN_MANAGEMENT',
            title: '⛓️ Supply Chain Management',
            content:
                'Select Supply Chain Management in the sidebar to access logistics, purchase orders, invoicing, and vendor options.',
            placement: 'right',
            skipBeacon: true,
            route: '/inventory/supply-chain-management/supplier-management',
        });
        steps.push({
            target: '.tour-INVENTORY_SUPPLIER_MANAGEMENT',
            title: '🤝 Vendor Management',
            content:
                'Select Vendor Management in the sub-menu to onboard and track your suppliers.',
            placement: 'right',
            skipBeacon: true,
            route: '/inventory/supply-chain-management/supplier-management',
        });
        steps.push({
            target: '.tour-add-vendor-btn',
            title: '➕ Add Vendors (Mandatory Step 1)',
            content:
                'First, you must onboard your vendors. Click "Add Vendor" to enter their contact info, bank details, and SLA metrics.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/inventory/supply-chain-management/supplier-management',
        });
        steps.push({
            target: '.tour-INVENTORY_SKU',
            title: '🏷️ SKU Management',
            content:
                'After vendors are set up, navigate to SKU Management to log raw materials.',
            placement: 'right',
            skipBeacon: true,
            route: '/inventory/sku',
        });
        steps.push({
            target: '.tour-add-sku-btn',
            title: '➕ Add SKUs (Mandatory Step 2)',
            content:
                'Second, define raw material SKUs. When adding a SKU, make sure to select the vendor who provides that item.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/inventory/sku',
        });
    }

    // Conditional POS steps (Mandatory Steps 3, 4, & 5)
    if (isModuleEnabled('POS')) {
        steps.push({
            target: '.tour-module-POS',
            title: '🍽️ POS Module',
            content:
                'Click this tab to enter Point of Sale (POS) Management to construct your menu catalog.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/pos/menu-management',
        });
        steps.push({
            target: '.tour-POS_MENU_MANAGEMENT',
            title: '🍽️ Menu Management',
            content:
                'Select Menu Management to manage categories, subcategories, dishes, and recipes.',
            placement: 'right',
            skipBeacon: true,
            route: '/pos/menu-management',
        });
        steps.push({
            target: '.tour-add-category-btn',
            title: '➕ Add Categories for Recipes (Mandatory Step 3)',
            content:
                'Third, create category and subcategory structures to categorize dishes and define raw material recipes.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/pos/menu-management',
        });
        steps.push({
            target: '.tour-add-dish-btn',
            title: '➕ Add Dishes (Mandatory Step 4)',
            content:
                'Fourth, click "+ ADD New Dish" to define your menu items, pricing, photos, and variant options.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/pos/menu-management',
        });
        steps.push({
            target: '.tour-manage-banners-btn',
            title: '🖼️ Banner Management (Mandatory Step 5)',
            content:
                'Fifth, click "Manage banners" to upload promotional images and campaigns displayed to POS customers.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/pos/menu-management',
        });
    }

    // Conditional CRM steps
    if (isModuleEnabled('CRM')) {
        steps.push({
            target: '.tour-module-CRM',
            title: '📣 CRM Module',
            content:
                'Click this tab to manage Customer Relations, including segments, time-based dining profiles, SMS outreach, and loyalty reward settings.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/crm/campaign-control',
        });
        steps.push({
            target: '.tour-CRM_CAMPAIGN_CONTROL',
            title: '📣 Campaign Control',
            content:
                'Launch email, SMS, and messaging outreach campaigns targeted at custom guest profiles to drive loyalty and repeat business.',
            placement: 'right',
            skipBeacon: true,
            route: '/crm/campaign-control',
        });
    }

    // Conditional Finance steps
    if (isModuleEnabled('FINANCE')) {
        steps.push({
            target: '.tour-module-FINANCE',
            title: '📈 Finance Module',
            content:
                'Click this tab to monitor financial health. Review total revenues, profit margins, sales tax compliance, and COGS details.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/finance/dashboard',
        });
        steps.push({
            target: '.tour-FINANCE_DASHBOARD',
            title: '📈 Finance Dashboard',
            content:
                'Track profit margins, gross revenues, net profits, and explore automated COGS analysis inside this analytics center.',
            placement: 'right',
            skipBeacon: true,
            route: '/finance/dashboard',
        });
    }

    // Conditional Restaurant Admin steps
    if (isModuleEnabled('RESTAURANT_ADMIN')) {
        steps.push({
            target: '.tour-module-RESTAURANT_ADMIN',
            title: '💳 Restaurant Admin Module',
            content:
                'Click this tab to enter Admin Controls, where you can modify configurations for billing settings, role permissions, multi-outlet settings, and audit logs.',
            placement: 'bottom',
            skipBeacon: true,
            route: '/restaurant-admin/payment-settings',
        });
        steps.push({
            target: '.tour-RESTAURANT_ADMIN_PAYMENT_SETTINGS',
            title: '💳 Payment Settings',
            content:
                'Set up taxation rates (GST/VAT), service fees, link UPI IDs, and upload merchant QR codes for POS billing screens.',
            placement: 'right',
            skipBeacon: true,
            route: '/restaurant-admin/payment-settings',
        });
    }

    // Add layout items at the end
    steps.push(
        {
            target: '.notification-actions',
            title: '🔔 Notifications & Alerts',
            content:
                'Stay updated with real-time order notifications, stock alerts, and AI insights. You can toggle the AI switch for smart notifications.',
            placement: 'bottom-end',
            skipBeacon: true,
        },
        {
            target: '.chat-btn',
            title: '✨ AI Chatbot Assistant',
            content:
                'Need instant analytics summaries, support, or help navigating? Click the sparkle icon to open the interactive AI assistant at any time.',
            placement: 'top',
            skipBeacon: true,
        },
        {
            target: '.profile-dd',
            title: '👤 Profile & Guide Replay',
            content:
                'Manage your user profile, view settings, or log out. If you ever need to view this interactive tour again, you can replay it from this dropdown.',
            placement: 'bottom-end',
            skipBeacon: true,
        }
    );

    return steps;
};
